import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ExtendedBaseTab } from '@common/classes/extended-base-tab';
import { AttachmentService } from '@common/services/attachment.service';
import { FileDownloadService } from '@common/services/file-download.service';
import { marker } from '@common/translate-marker';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ToastService } from '@services/toast.service';
import { CustomExtendedTabService } from 'app/singleton-services/custom-extended-tab.service';
import { NgxSpinnerService } from 'ngx-spinner';

const BYTES_IN_MB = 1024 * 1024;

@Component({
    selector: 'app-tab-attachments',
    templateUrl: './tab-attachments.component.html',
    styleUrls: ['./tab-attachments.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabAttachmentsComponent extends ExtendedBaseTab implements OnInit {
    @Input() entity: string;
    @Input() id: number;
    @Input() active = true;
    @Input() showRestrictedButtons = true;
    attachmentsArray: any[] = [];

    dialogAddAttachment = false;
    // the restrictions are defaulted in ExtendedBaseTab; the tooltip shows the configured ones only
    attachmentSettingsLoaded = false;
    submittingAttachment = false;
    fileToSent: string = null;
    fileName: string = null;
    @ViewChild('attachmentDescriptionTextArea')
    fileDescription: TextAreaComponent;
    // remove attachments
    dialogRemoveAttachments = false;
    removingAttachments = false;

    constructor(
        private toastyService: ToastService,
        customExtendedTabService: CustomExtendedTabService,
        public service: AttachmentService,
        public fileDownloadService: FileDownloadService,
        private spinner: NgxSpinnerService
    ) {
        super(customExtendedTabService);
    }

    ngOnInit() {
        this.loadAttachmentSettings();
        this.defaultPaging();
        this.getData();
    }

    get attachmentTooltip(): string {
        const types = this.attachmentFileUploadRestrictions.allowedExtensions
            .map((ext) => ext.replace('.', '').toUpperCase())
            .join(', ');
        const maxSizeMb = Number((this.attachmentFileUploadRestrictions.maxFileSize / BYTES_IN_MB).toFixed(2));
        return (
            `${this.translateService.instant(marker('Attachment Type'))}: ${types} | ` +
            `${this.translateService.instant(marker('Max File Size Allowed (MB)'))}: ${maxSizeMb}`
        );
    }

    public addAttachment() {
        if (!this.fileToSent) return;

        if (this.submittingAttachment) {
            return;
        }
        this.submittingAttachment = true;
        this.spinner.show();

        const body = {
            id: this.id,
            entity: this.entity,
            fileContent: this.fileToSent,
            fileName: this.fileName,
            fileDescription: this.fileDescription.value
        };

        this.service.addAttachment(body).subscribe({
            next: () => {
                this.removeSelectedFile();
                this.closeDialogAddAttachment();
                this.submittingAttachment = false;
                this.toastyService.success(this.translateService.instant(marker('Attachment added successfully.')));
                this.getData();
                this.spinner.hide();
            },
            error: () => {
                this.submittingAttachment = false;
                this.spinner.hide();
            }
        });
    }

    public removeAttachments() {
        if (this.removingAttachments) {
            return;
        }
        this.removingAttachments = true;
        this.spinner.show();

        const body = { ids: this.selected, entity: this.entity };

        this.service.removeAttachment(body).subscribe({
            next: () => {
                this.closeDialogRemoveAttachments();
                this.selected = [];
                this.removingAttachments = false;
                this.toastyService.success(this.translateService.instant(marker('Attachment removed successfully.')));
                this.getData();
                this.spinner.hide();
            },
            error: (error) => {
                this.toastyService.error(error.error.errorMessage);
                this.closeDialogRemoveAttachments();
                this.selected = [];
                this.removingAttachments = false;
                this.spinner.hide();
            }
        });
    }

    public fileSelected($event) {
        // a new selection replaces the previous one, so a rejected file must not leave the old content behind
        this.removeSelectedFile();
        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    this.translateService.instant(marker('Attachment size invalid')) + ' - ' + element.name
                );
                return;
            }
            if (
                !this.attachmentFileUploadRestrictions.allowedExtensions.some(
                    (ext) => ext == (element.extension || '').toLowerCase()
                )
            ) {
                this.toastyService.error(
                    this.translateService.instant(marker('Invalid attachment')) + ' - ' + element.name
                );
                return;
            }
            const raw = element.rawFile;
            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    public openDialogAddAttachment() {
        this.dialogAddAttachment = true;
    }

    public closeDialogAddAttachment() {
        this.removeSelectedFile();
        this.dialogAddAttachment = false;
    }

    public openDialogRemoveAttachments() {
        this.dialogRemoveAttachments = true;
    }

    public closeDialogRemoveAttachments() {
        this.dialogRemoveAttachments = false;
    }

    public fileRemoved($event) {
        this.removeSelectedFile();
    }

    downloadAttachment(data: any) {
        if (data) {
            this.fileDownloadService.handleAttachmentDownload(data);
        }
    }

    canRemove() {
        return (
            this.active &&
            this.selected.length > 0 &&
            (this.attachmentsArray.filter((x) => this.selected.includes(x.id)).every((x) => x.canRemove) ||
                this.userHasRole('Administrator'))
        );
    }

    private removeSelectedFile() {
        this.fileToSent = null;
        this.fileName = null;
    }

    private documentOnLoad(file: string, name: string) {
        this.fileToSent = file;
        this.fileName = name;
    }

    private loadAttachmentSettings(): void {
        this.service.getAttachmentSettingsForUpload().subscribe((response) => {
            this.attachmentFileUploadRestrictions = response as FileRestrictions;
            this.attachmentSettingsLoaded = true;
        });
    }

    private getData() {
        this.service
            .getAttachments(this.entity, this.id)
            .pipe()
            .subscribe({
                next: (response) => {
                    this.attachmentsArray = response;
                    this.loadItems(this.attachmentsArray);
                    this.isBusy = false;
                }
            });
    }
}
